/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
  Typography,
} from '@material-ui/core';

// icons
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

import useStyles from '../styles';
import Modal from '../../Modal';
import ModalButton from '../../Button';
import QuestionShell from '../QuestionShell';
import useQuestionForm, { QUESTION_FIELDS } from '../QuestionShell/useQuestionForm';

import { VIEW_MODE } from '../../../utils/const/taskModes';

function YesNoQuestion({
  identifier,
  pages,
  info,
  handleDeleteQuestion,
  mode,
  switchStock,
  setSwitchStock,
  isSwitchStock,
  handleDuplicateQuestion,
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [correct, setCorrect] = useState(['1']);
  const [switchStockConfirmation, setSwitchStockConfirmation] = useState(false);
  const [tempSwitchStock, setTempSwitchStock] = useState(null);

  // si no viene en la info (AKA pregunta), se toma de la página (AKA tarea)
  const [options, setOptions] = useState(
    info
      ? info.QuestionChoices
      : [
        {
          identifier: 1,
          title: t('task.taskEdition.yesNoQuestionText.yes'),
          type: 'text',
        },
        {
          identifier: 2,
          title: 'No',
          type: 'text',
        },
      ],
  );

  const question = useQuestionForm({
    identifier, pages, info, autoLoadMedia: false,
  });
  const {
    image, setImage, setEmailsArray, setSendEmail,
    scheduleWrite, writePage, questionLogic, syncLocalField,
  } = question;

  // Siembra el Sí/No y la alternativa correcta por defecto en una pregunta nueva.
  // Va antes del efecto de carga a propósito: ese lee `questionChoices` de la
  // página y, sin esto, una pregunta recién agregada se quedaría sin opciones.
  // Antes se hacía mutando `pages` durante el render, que rompe en Strict Mode.
  useEffect(() => {
    if (!pages) return;
    // eslint-disable-next-line no-param-reassign
    pages[identifier - 1] = {
      ...pages[identifier - 1],
      questionAnswer: correct,
      questionChoices: pages[identifier - 1].questionChoices ?? options,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (info) {
      if (image.url !== info.imageUrl || image.name !== info.imageKey) {
        setImage({
          ...image,
          url: info.imageUrl,
          name: info.imageKey,
        });
      }
      if (
        info.QuestionEmails !== undefined
        && info.QuestionEmails.length !== 0
      ) {
        setEmailsArray(info.QuestionEmails[0].emails.split(','));
      }
      setSendEmail(info.sendEmail);
      if (
        info.CorrectQuestionChoices[0]
        && info.CorrectQuestionChoices[0].questionChoiceId
      ) {
        setCorrect(
          Number.parseInt(info.CorrectQuestionChoices[0].questionChoiceId, 10),
        );
      }
      const sortedChoices = [...info.QuestionChoices].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
      );
      const optionsWithIdentifiers = sortedChoices.map((choice, index) => ({
        ...choice,
        identifier: choice.identifier ?? index + 1,
      }));
      setOptions(optionsWithIdentifiers);
    } else if (pages && pages[identifier - 1].questionChoices) {
      const { questionChoices } = pages[identifier - 1];

      // Igual que en los demás tipos con alternativas: las borradas se conservan
      // en la página con su flag pero no vuelven a la lista editable.
      const deleted = questionChoices.filter((choice) => choice.deleted);
      const sortedChoices = questionChoices
        .filter((choice) => !choice.deleted)
        .sort((a, b) => (a.position ?? 999) - (b.position ?? 999));

      const optionsArray = sortedChoices.map((questionChoice, index) => ({
        title: questionChoice.title,
        type: questionChoice.type,
        identifier: index + 1,
        id: questionChoice.id,
        position: questionChoice.position ?? index,
      }));

      if (pages[identifier - 1].correctQuestionChoices
        && pages[identifier - 1].correctQuestionChoices.length > 0) {
        const correctQuestionChoiceId = pages[identifier - 1].correctQuestionChoices[0]
          .questionChoiceId;
        const correctIndex = optionsArray.findIndex(
          (questionChoice) => questionChoice.id === correctQuestionChoiceId,
        );
        setCorrect(Number.parseInt(correctIndex + 1, 10));
        pages[identifier - 1] = {
          ...pages[identifier - 1],
          questionAnswer: [Number.parseInt(correctIndex + 1, 10)],
        };
      }
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        questionChoices: [...optionsArray, ...deleted],
        questionAnswer: correct,
      };
      setOptions(optionsArray);
      if (
        pages[identifier - 1].questionEmails !== undefined
        && pages[identifier - 1].questionEmails.length !== 0
      ) {
        setEmailsArray(
          pages[identifier - 1].questionEmails[0].emails.split(','),
        );
      }
      setSendEmail(pages[identifier - 1].sendEmail);
    } else {
      setOptions([]);
    }

    if (pages && pages[identifier - 1].imageUrl) {
      setImage({
        ...image,
        url: pages[identifier - 1].imageUrl,
        name: pages[identifier - 1].imageKey,
      });
    }
    // eslint-disable-next-line
  }, [identifier, info, pages]);

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    syncLocalField(id, value);

    if (QUESTION_FIELDS.includes(id)) {
      scheduleWrite({
        [id]: value,
        questionLogic,
        questionAnswer: correct,
        imageUrl,
      });
      return;
    }

    const indexOption = options.findIndex(
      (option) => option.identifier === id,
    );
    const newOptions = indexOption === -1
      ? options
      : options.map((option, index) => (
        index === indexOption ? { ...option, title: value } : option
      ));

    if (indexOption !== -1) setOptions(newOptions);

    scheduleWrite({
      questionChoices: newOptions,
      questionAnswer: correct,
    });
  }, [correct, options, questionLogic, scheduleWrite, syncLocalField]);

  const handleCorrectAnswer = (event, identifierid) => {
    if (event.target.checked) {
      setCorrect([identifierid]);
      // El valor nuevo, no `correct`: leerlo acá guardaba la alternativa que
      // estaba marcada antes del clic.
      writePage({ questionAnswer: [identifierid] });
    }
  };

  const handleCheckSwitchStock = (event) => {
    const { checked, id } = event.target;

    if (switchStock !== null && switchStock !== id && checked) {
      setTempSwitchStock(id);
      setSwitchStockConfirmation(true);
      return;
    }

    if (checked) {
      setSwitchStock(id);
    } else {
      setSwitchStock(null);
    }
  };

  const confirmSwitchStockChange = () => {
    setSwitchStock(tempSwitchStock);
    setSwitchStockConfirmation(false);
  };

  const cancelSwitchStockChange = () => {
    setSwitchStockConfirmation(false);
  };

  const isCorrect = (identifierid) => {
    if (!correct) return false;
    return Number.parseInt(correct, 10) === Number.parseInt(identifierid, 10);
  };

  return (
    <div>
      <FormControlLabel
        control={
          <Checkbox
            id={`${identifier}`}
            color="primary"
            name="switchStock"
            onChange={(event) => handleCheckSwitchStock(event)}
            checked={`${identifier}` === `${switchStock}` && isSwitchStock}
            disabled={!isSwitchStock}
          />
        }
        label={t('task.taskEdition.yesNoQuestionText.switchStock')}
      />
      <QuestionShell
        {...question}
        handleOnChange={handleOnChange}
        identifier={identifier}
        info={info}
        mode={mode}
        titleKey="task.taskEdition.yesNoQuestionText.question"
        descriptionKey="task.taskEdition.yesNoQuestionText.description"
        handleDeleteQuestion={handleDeleteQuestion}
        handleDuplicateQuestion={handleDuplicateQuestion}
      >
        {options.map((option) => (
          <div key={option.identifier || option.id}>
            <FormControlLabel
              control={
                <Checkbox
                  className={styles.checkbox}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={
                    <RadioButtonCheckedIcon style={{ color: 'green' }} />
                  }
                  name={`${option.identifier}`}
                  onClick={(event) => handleCorrectAnswer(event, option.identifier)}
                  checked={isCorrect(option.identifier || `${option.id}`)}
                  disabled={mode === VIEW_MODE}
                />
              }
              label={
                <TextField
                  id={`${option.identifier}`}
                  className={styles.option}
                  value={option.title}
                  placeholder={`${t(
                    'task.taskEdition.simpleSelectionText.write',
                  )} ${t('task.taskEdition.simpleSelectionText.option')}`}
                  InputProps={{ disableUnderline: true }}
                  disabled
                  multiline
                  maxRows={5}
                  fullWidth
                />
              }
            />
          </div>
        ))}
      </QuestionShell>
      {switchStockConfirmation ? (
        <Modal
          title={t('task.taskEdition.yesNoQuestionText.modalTitle')}
          defaultOpen={switchStockConfirmation}
          setCurrentOpen={setSwitchStockConfirmation}
        >
          <div className={styles.dialogWrapper}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12}>
                <Typography
                  className={styles.dialogText}
                  variant="h2"
                  color="textSecondary"
                  component="p"
                >
                  {t(
                    'task.taskEdition.yesNoQuestionText.confirmSwitchStockChange',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={3}>
                <ModalButton
                  id="confirm-btn"
                  onClick={confirmSwitchStockChange}
                  variant="contained"
                  text={t('task.taskEdition.yesNoQuestionText.confirm')}
                  height="35px"
                  width="100%"
                  backgroundColor="#FFFFFF"
                  color="#2C9DC9"
                  borderRadius="10px"
                />
              </Grid>
              <Grid item xs={3}>
                <ModalButton
                  id="cancel-btn"
                  onClick={cancelSwitchStockChange}
                  variant="contained"
                  text={t('task.taskEdition.yesNoQuestionText.cancel')}
                  height="35px"
                  width="100%"
                  backgroundColor="#F5B7B1"
                  color="#E74C3C"
                  borderRadius="10px"
                />
              </Grid>
            </Grid>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export default YesNoQuestion;
